import React from "react";
import { Box, Tooltip, Typography } from "@mui/material";

export interface BarSeries {
    key: string;
    label: string;
    color: string;
}

export interface BarDatum {
    /** Short label under the bar, e.g. "Oct 5". */
    label: string;
    /** Longer label for the tooltip, e.g. "Week of Oct 5". */
    title?: string;
    values: Record<string, number>;
}

interface BarChartProps {
    data: BarDatum[];
    series: BarSeries[];
    /** Stack the series in one bar (true) or put them side by side. */
    stacked?: boolean;
    height?: number;
    formatValue?: (n: number) => string;
}

/**
 * A small bar chart built from plain boxes — no chart library. Bars keep a
 * minimum width and the chart scrolls sideways inside its own box when
 * there are many, so the page never gets wider than a phone.
 */
export const BarChart: React.FC<BarChartProps> = ({
    data,
    series,
    stacked = false,
    height = 160,
    formatValue = (n) => n.toLocaleString(),
}) => {
    const groupTotal = (d: BarDatum) =>
        stacked
            ? series.reduce((sum, s) => sum + (d.values[s.key] || 0), 0)
            : Math.max(0, ...series.map((s) => d.values[s.key] || 0));
    const max = Math.max(0, ...data.map(groupTotal));
    const scale = (v: number) => (max > 0 ? Math.max(v > 0 ? 2 : 0, (v / max) * height) : 0);
    // Label every bar while they fit, then thin them out.
    const labelEvery = data.length > 24 ? Math.ceil(data.length / 12) : data.length > 14 ? 2 : 1;
    const barWidth = stacked ? 22 : 10;
    const groupWidth = stacked ? barWidth : barWidth * series.length + 2 * (series.length - 1);

    return (
        <Box>
            <Box sx={{ overflowX: "auto", pb: 0.5 }}>
                <Box sx={{ display: "flex", alignItems: "stretch", gap: 0.5, minWidth: "max-content", px: 0.5 }}>
                    {/* Scale: the top value and the baseline */}
                    <Box
                        sx={{
                            display: "flex",
                            flexDirection: "column",
                            justifyContent: "space-between",
                            height,
                            pr: 0.75,
                            borderRight: 1,
                            borderColor: "divider",
                        }}
                    >
                        <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1 }}>
                            {formatValue(max)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1 }}>
                            0
                        </Typography>
                    </Box>
                    {data.map((d, i) => {
                        const tip = (
                            <Box>
                                <Typography variant="caption" fontWeight={700} display="block">
                                    {d.title || d.label}
                                </Typography>
                                {series.map((s) => (
                                    <Typography key={s.key} variant="caption" display="block">
                                        {s.label}: {formatValue(d.values[s.key] || 0)}
                                    </Typography>
                                ))}
                            </Box>
                        );
                        return (
                            <Tooltip key={i} title={tip} placement="top" arrow>
                                <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", cursor: "default" }}>
                                    <Box
                                        sx={{
                                            height,
                                            width: groupWidth,
                                            display: "flex",
                                            // Stacked: a column built up from the baseline.
                                            // Side by side: a row of bars standing on it.
                                            flexDirection: stacked ? "column-reverse" : "row",
                                            justifyContent: stacked ? "flex-start" : "center",
                                            alignItems: stacked ? "center" : "flex-end",
                                            gap: stacked ? 0 : "2px",
                                            "&:hover > *": { opacity: 0.85 },
                                        }}
                                    >
                                        {series.map((s) => (
                                            <Box
                                                key={s.key}
                                                sx={{
                                                    width: barWidth,
                                                    height: scale(d.values[s.key] || 0),
                                                    bgcolor: s.color,
                                                    borderRadius: stacked ? 0 : "3px 3px 0 0",
                                                    flexShrink: 0,
                                                }}
                                            />
                                        ))}
                                    </Box>
                                    <Typography
                                        variant="caption"
                                        color="text.secondary"
                                        sx={{
                                            mt: 0.5,
                                            fontSize: 10.5,
                                            whiteSpace: "nowrap",
                                            visibility: i % labelEvery === 0 ? "visible" : "hidden",
                                            minWidth: groupWidth,
                                            textAlign: "center",
                                        }}
                                    >
                                        {d.label}
                                    </Typography>
                                </Box>
                            </Tooltip>
                        );
                    })}
                </Box>
            </Box>
            {series.length > 1 && (
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5, mt: 1 }}>
                    {series.map((s) => (
                        <Box key={s.key} sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                            <Box sx={{ width: 10, height: 10, borderRadius: "2px", bgcolor: s.color }} />
                            <Typography variant="caption" color="text.secondary">
                                {s.label}
                            </Typography>
                        </Box>
                    ))}
                </Box>
            )}
        </Box>
    );
};
